import Dispatcher from "Dispatcher";
import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import typeCheckingStore from "data/util/typeCheckingStore";
import { hotLoadObject } from "data/storeUtils";
import CrossAppActions from "data/CrossAppActions";
import LoadObject from "data/LoadObject";
import dotProp from "dot-prop-immutable";
import DataManager from "data/DataManager";
import userStore from "data/userStore";
import TableAccess from "data/util/TableAccess";
import TableStore from "data/TableStore";
import MapDialogActions from "data/MapDialogActions";
import entityFieldMetaStore from "./meta/entityFieldMetaStore";
import entityMetaStore from "./meta/entityMetaStore";
import { isAutoManaged} from "data/util/EntityProxy";
import SuperRecordListStore from "./SuperRecordListStore";
import { clientFilteredList } from "data/SuperRecordActions";
import Q from "lib/Q";
import { infoTypeToFieldType } from "data/util/infoTypeToFieldType";


const getPagination = (state, query) =>
    state.pagination && state.pagination.hasOwnProperty(query)
        ? state.pagination[query]
        : {
            page: 1,
            pageCount: 0,
            pageSize: 25,
            totalResultCount: 0,
        };

const getQueryLO = (state, query) =>
    state.queries.hasOwnProperty(query)
        ? state.queries[query]
        : LoadObject.empty();

const idsToRecordsBuilder = (state, entityKey) => {
    const access = new TableAccess(state);
    return ids => ids.map(id => access.getEntityProxy(entityKey, id));
};

class CrossAppStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {
            tables: {},
            queries: {},
            pagination: {}
        };
    }

    reduce(state, action) {
        switch (action.type) {

            case CrossAppActions.SEARCH: {
                const lo = getQueryLO(state, action.query);
                const entityNameBits = action.entity.split("/");
                const impl = entityNameBits[0];
                const version = entityNameBits[1];
                const entity = entityNameBits[2];
                return dotProp.set(
                    state,
                    ["queries", action.query],
                    lo.loading(DataManager.crossAppSearch(
                        userStore.getActiveOrgKey(),
                        action.stereotype,
                        impl,
                        version,
                        entity,
                        action.query,
                        action.page,
                        action.pageSize,
                        action.interactive,
                    )),
                );
            }

            case CrossAppActions.SEARCH_RESULT: {
                // add response pagination info
                const newState = dotProp.set(
                    state,
                    ["pagination", action.query],
                    {
                        page: action.data.page,
                        pageCount: action.data.pageCount,
                        pageSize: action.data.pageSize,
                        query: action.data.query,
                        totalResultCount: action.data.totalResultCount,
                    }
                );
                const access = new TableAccess(newState);
                for (const result of action.data.results) {
                    this.doAutomanagement(result);
                    access.loadSuperRecordPacket(result, `${action.impl}/${action.version}`);
                }
                return dotProp.set(
                    access.getState(),
                    ["queries", action.query],
                    lo => lo.setValue(action.data.results.map(it => it.id)).done(),
                );
            }

            case CrossAppActions.SEARCH_ERROR: {
                return dotProp.set(
                    state,
                    ["queries", action.query],
                    lo => lo.setError(action.error).done(),
                );
            }

            case MapDialogActions.SELECT_PLUGIN: {
                return this.getInitialState(); // per Helen's decree
            }

            case MapDialogActions.SELECT_STEREOTYPE: {
                return this.getInitialState();
            }

            default:
                return state;
        }
    }
    getPagination(query) {
        return getPagination(this.getState(), query);
    }

    getColdLO(query, entity) {
        return getQueryLO(this.getState(), query)
            .map(idsToRecordsBuilder(this.getState(), entity));
    }

    getHotLO(query, params, stereotype, entity, interactive) {
        const page = params.pagenumber !== "undefined" ? params.pagenumber : null;
        const pageSize = params.pagesize !== "undefined" ? params.pagesize : null;

        return hotLoadObject(
            () => getQueryLO(this.getState(), query),
            () => Dispatcher.dispatch({
                type: CrossAppActions.SEARCH,
                query,
                interactive,
                stereotype,
                entity,
                page,
                pageSize
            }),
            lo => interactive || lo.isEmpty()
        ).map(idsToRecordsBuilder(this.getState(), entity));
    }

    // for automanaged fields mapping requires that the ids of the assocaited records are within the set of available ids in the local
    // records for this hybrid instance, but with cross-app mapping the sources records are not from the local instance
    // so we need to look up the associated records by their key fields and set the ids accordingly before loading the records
    // this is a best-effort approach and will throw an error if no match is found
    doAutomanagement(packet) {
        for (const tableKey of Object.keys(packet.tables)) {
            //const table = packet.tables[tableKey];
            const entityInfos = entityMetaStore.getInfosForLogicalTable(tableKey);
            const access = new TableAccess(TableStore.getState());
            const autoManageByColumn = this.buildFieldInfosByColumn(
                entityInfos,
                isAutoManaged,
            );
            if (Object.keys(autoManageByColumn).length > 0) {
                for (const fis of Object.values(autoManageByColumn)) {
                    for (const fieldInfo of fis) {
                        const mfi = entityFieldMetaStore.getInfo(
                            fieldInfo.otherEntityKey,
                            fieldInfo.autoManageKey,
                        );
                        
                        // parent entity info - we're going to need this later for the stored in table 
                        const pEK = mfi.entityKey;
                        const parentInfo = entityMetaStore.getInfo(pEK);
                        
                        // global entity info
                        const gEK = mfi.otherEntityKey;
                        const targetGlobalsLO = clientFilteredList(
                            SuperRecordListStore,
                            access,
                            gEK,
                        );
                        if (!targetGlobalsLO.hasValue()) return;
                        // get the entire list of global candidates for the field
                        const targetGlobals = targetGlobalsLO.getValueEnforcing();

                        // get the key fields to match the source and target on
                        const globalInfo = entityMetaStore.getInfo(gEK);
                        const keyFields = globalInfo.keys ? globalInfo.keys[0].fieldKeys : [];

                        // parent source record table
                        // add it if it doesn't exist otherwise things will fall over in access.loadSuperRecordPacket
                        if (!packet.tables.hasOwnProperty(parentInfo.tableKey)) {
                            packet.tables[parentInfo.tableKey] = {};
                        }
                        const pSourceRecordTable = packet.tables[parentInfo.tableKey];

                        // global source record table
                        // add it if it doesn't exist otherwise things will fall over in access.loadSuperRecordPacket
                        if (!packet.tables.hasOwnProperty(globalInfo.tableKey)) {
                            packet.tables[globalInfo.tableKey] = {};
                        }
                        const gSourceRecordTable = packet.tables[globalInfo.tableKey];

                        // iterate through the sources and find the matching target
                        for (const gSourceRecordKey of Object.keys(gSourceRecordTable)) {
                            const gSourceRecord = gSourceRecordTable[gSourceRecordKey];
                            const query = Q.conjunction(keyFields
                                .map((fn) => entityFieldMetaStore.getInfo( // switch to fieldInfo
                                    mfi.otherEntityKey,
                                    fn,
                                ))
                                .filter((fi) => fi.type !== "FileField") // exclude file fields
                                .map((fi) => Q.ref(fi.key).eq(
                                    Q.value(
                                        gSourceRecord[fi.key],
                                        infoTypeToFieldType(fi.type),
                                    ),
                                ))
                            );
                            const targetGlobal = targetGlobals.find(query.test);
                            if (targetGlobal == null) {
                                throw new Error(`No ${globalInfo.tableKey} match found for ${gSourceRecord._id}`);
                            }
                            // update the source table map and the gSourceRecord _id to match the target
                            gSourceRecord._id = targetGlobal.id;
                            gSourceRecordTable[targetGlobal.id] = gSourceRecord;

                            delete gSourceRecordTable[gSourceRecordKey];

                            // now find the parent record referencing the replaced global id and update it as well.
                            for (const pSourceRecordKey of Object.keys(pSourceRecordTable)) {
                                const pSourceRecord = pSourceRecordTable[pSourceRecordKey];
                                if (pSourceRecord[mfi.logicalColumn].toString() === gSourceRecordKey) {
                                    pSourceRecord[mfi.logicalColumn] = targetGlobal.id;
                                }
                            }
                        }

                        // After processing existing records, add any targetGlobals that are missing from gSourceRecordTable
                        for (const targetGlobal of targetGlobals) {
                            if (!gSourceRecordTable.hasOwnProperty(targetGlobal.id)) {
                                // Add the targetGlobal to gSourceRecordTable
                                const globalRecord = {
                                    _id: targetGlobal.id,
                                };
                                // Copy all field values from the targetGlobal EntityProxy
                                for (const fieldKey of keyFields) {
                                    const fieldInfo = entityFieldMetaStore.getInfo(gEK, fieldKey);
                                    if (fieldInfo) {
                                        globalRecord[fieldInfo.key] = targetGlobal.get(fieldInfo.key);
                                    }
                                }
                                gSourceRecordTable[targetGlobal.id] = globalRecord;

                                // Add a corresponding parent record to pSourceRecordTable
                                // Generate a new ID for the parent record
                                const newParentId = -(Date.now() + Math.floor(Math.random() * 10000));
                                pSourceRecordTable[newParentId] = {
                                    _id: newParentId,
                                    [mfi.logicalColumn]: targetGlobal.id,
                                };
                            }
                        }
                    }
                }
            }
        }
    }

    buildFieldInfosByColumn(entityInfos, optionalFieldFilter = null) {
        const fieldInfosByColumn = {};
        for (const ei of entityInfos) {
            for (const f of entityFieldMetaStore.getAllInfos(ei.key, optionalFieldFilter)) {
                if (fieldInfosByColumn.hasOwnProperty(f.logicalColumn)) {
                    fieldInfosByColumn[f.logicalColumn].push(f);
                } else {
                    fieldInfosByColumn[f.logicalColumn] = [f];
                }
            }
        }
        return fieldInfosByColumn;
    }
}

CrossAppStore.stateTypes = {
    tables: PropTypes.object.isRequired,
    queries: PropTypes.objectOf(PropTypes.instanceOf(LoadObject)).isRequired,
};

export default typeCheckingStore(new CrossAppStore());



// WEBPACK FOOTER //
// ./app/data/CrossAppStore.js